import { mergePropertiesWithDefaults, selectKeys } from "./frontendlib/utils";

// if process.env.NODE_ENV === "test"
export const isTestError = (error) => {
  // NOTE(Austin): Annoyingly, assertion errors will loop back here in
  // tests, so we have to ignore these.
  return error != null && error.constructor.name === "AssertionError";
};
// #endif

export class Warning extends Error {
  constructor(...params) {
    super(...params);
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, Warning);
    }
    this.severity = "warning";
  }
}

const tryConvertStringToNumber = (str) => {
  if (str == null) {
    return 0;
  }

  const parsed = parseInt(str);
  if (Number.isNaN(parsed)) {
    return 0;
  }

  return parsed;
};

export const tryParseStackInfo = (stack) => {
  if (stack == null) {
    return null;
  }

  const match = String(stack).match(
    // NOTE(Austin): We include about:srcdoc for inner_env errors.
    /(\b(?:[^ ]*\.js.*?)|(?:about:srcdoc)):(\d+)?:?(\d+)?/
  );

  if (match == null) {
    return null;
  }

  // Line and column need to be integers. We have them in our adexp
  // protobuf this way, so if they aren't we'll get a malformed message.
  return {
    sourceURL: match[1],
    line: tryConvertStringToNumber(match[2]),
    column: tryConvertStringToNumber(match[3]),
  };
};

/**
 * Converts an Error or string into a map like
 * { message, stack, sourceURL, line, column }
 */
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/Error
export const normalizeError = (obj) => {
  if (obj == null) {
    return null;
  }

  if (typeof obj === "string") {
    return {
      message: obj,
    };
  }

  const errorProps = selectKeys(obj, Object.getOwnPropertyNames(obj));

  return {
    ...tryParseStackInfo(errorProps.stack),
    ...errorProps,
  };
};

/**
 * Converts an ErrorEvent object to a map like:
 * { message, line, column, sourceURL }
 * plus any additional information the error object contains.
 */
export const normalizeErrorEvent = ({
  // https://developer.mozilla.org/en-US/docs/Web/API/ErrorEvent
  message,
  filename,
  lineno,
  colno,
  error,
}) => {
  try {
    // Possible keys in an ErrorEvent:
    // [stack, name, fileName, lineNumber, columnNumber]
    const errorProps =
      error != null ? selectKeys(error, Object.getOwnPropertyNames(error)) : {};

    const errorObj = {
      message,
      severity: error && error.severity ? error.severity : "error",
      line: lineno,
      column: colno,
      sourceURL: filename,
      ...errorProps,
    };

    if (error == null) {
      return errorObj;
    }

    const parsedInfo = tryParseStackInfo(error.stack);
    return mergePropertiesWithDefaults(errorObj, parsedInfo);
  } catch (e) {
    console.error(`Error in normalizeErrorEvent: ${e.stack}`);
    return null;
  }
};

/**
 * Converts an PromiseRejectionEvent object to a map like
 * { message, line, column, sourceURL }
 * plus any additional information the error object contains.
 */
export const normalizePromiseRejectionEvent = ({
  // https://developer.mozilla.org/en-US/docs/Web/API/PromiseRejectionEvent
  reason,
}) => {
  try {
    const reasonIsError = reason instanceof Error;
    const reasonIsString = typeof reason === "string";

    const errorMessage = reasonIsString ? reason : reason.message;

    // Possible keys in object (* are non-standard):
    // [message, stack*, name, fileName*, lineNumber*, columnNumber*]
    const errorProps = reasonIsError
      ? selectKeys(reason, Object.getOwnPropertyNames(reason))
      : {};

    const errorObj = {
      message: errorMessage,
      ...errorProps,
    };

    return mergePropertiesWithDefaults(
      errorObj,
      tryParseStackInfo(reason.stack)
    );
  } catch (e) {
    console.error(`Error in normalizePromiseRejectionEvent: ${e.stack}`);
    return null;
  }
};

/**
 * Normalizes the error and returns true if the error should be ingored;
 * otherwise, returns false.
 */
export const shouldIgnoreError = (errorObj) => {
  // Report only errors with non-malformed sourceURL.
  if (errorObj == null || errorObj.sourceURL === "undefined") {
    return true;
  }

  // mraid.js from adverty.com is a buggy implemenation. SMAATO and PUBMATIC are
  // the exchanges using such version and we see strange errors from the most.
  // While we are working with them to get a good MRAID implementation we filter
  // the noisy error messages by checking where the mraid.js is from.
  const stackFilter = new RegExp("adverty\\.com/.*mraid\\.js");

  // We see these errors quite often, and they are not related to our code.
  const regexFilters = [
    "Cannot read properties of undefined \\(reading 'fireEvent'\\)",
    "Cannot read property 'fireEvent' of undefined",
    "showInterstitial",
    "al_onPoststitialDismiss",
    "AFMA_ReceiveMessage",
    "webviewDidAppear",
    "AFMA_updateActiveView is not a function",
    "al_onAdViewRendered",
    "mraidbridge",
    "MoatMAK",
    "$adgIOS",
    "googqscp",
    "inmobi",
    "Script error",
    "avidbridge",
  ];
  const messageFilter = new RegExp(regexFilters.join("|"));
  const errorStr = errorObj.message || errorObj.toString();
  if (messageFilter.test(errorStr) || stackFilter.test(errorObj.stack)) {
    return true;
  }

  const normalized = selectKeys(errorObj, [
    "message",
    "line",
    "column",
    "stack",
    "sourceURL",
  ]);

  const nKeys = Object.keys(normalized).length;
  const onlyMessage =
    "message" in errorObj &&
    nKeys === 1 &&
    errorObj.message === "undefined is not a function";

  const onlyMessageStack =
    "stack" in errorObj &&
    "message" in errorObj &&
    nKeys === 2 &&
    errorObj.stack === "" &&
    errorObj.message === "undefined is not a function";

  const noSourceURL = errorObj.sourceURL == null && nKeys === 5;
  return onlyMessage || onlyMessageStack || noSourceURL;
};



// WEBPACK FOOTER //
// ./src/js/error_handling.js